import { useState, useCallback, useEffect } from 'react';
import apiService from '../../services/apiService';
import { INPUT_DEFAULTS } from '../common/inputDefaults';

// Hook for managing authentication state
export const useAuth = () => {
  const [userId, setUserId] = useState(() => {
    const saved = sessionStorage.getItem('userId');
    return saved ? parseInt(saved) : null;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return sessionStorage.getItem('userId') !== null;
  });

  const login = async (credentials) => {
    try {
      const response = await apiService.login(credentials);
      sessionStorage.setItem('userId', response.user_id);
      sessionStorage.setItem('sessionToken', response.session_token);  // 2026-04-02: single-session token
      setUserId(response.user_id);
      setIsLoggedIn(true);
      return { success: true, userId: response.user_id };
    } catch (error) {
      throw new Error(`Login failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('sessionToken');  // 2026-04-02: clear session token
    setUserId(null);
    setIsLoggedIn(false);
  };

  const createUser = async (userData) => {
    try {
      const response = await apiService.createUser(userData);
      sessionStorage.setItem('userId', response.user_id);
      sessionStorage.setItem('sessionToken', response.session_token);  // 2026-06-11: single-session token on signup
      setUserId(response.user_id);
      setIsLoggedIn(true);
      return { success: true, userId: response.user_id };
    } catch (error) {
      throw new Error(`Account creation failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 2026-06-17: Exchange a WorkOS web access token for a RothGPT session (Phase 3).
  // Mirrors login(): stores the same userId + sessionToken the rest of the app relies on.
  const loginWithWorkOS = async (accessToken) => {
    try {
      const response = await apiService.webSession(accessToken);
      sessionStorage.setItem('userId', response.user_id);
      sessionStorage.setItem('sessionToken', response.session_token);
      setUserId(response.user_id);
      setIsLoggedIn(true);
      return { success: true, userId: response.user_id, created: !!response.created };
    } catch (error) {
      throw new Error(`WorkOS login failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  return {
    userId,
    isLoggedIn,
    login,
    logout,
    createUser,
    loginWithWorkOS
  };
};

// Hook for managing user data
export const useUserData = (userId) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUser(userId);
      setUserInfo(data);
    } catch (err) {
      setError(`Failed to fetch user info: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return {
    userInfo,
    loading,
    error,
    refetch: fetchUserData
  };
};

// Hook for managing input data and assumptions
export const useInputData = (userId) => {
  const [inputInfo, setInputInfo] = useState(null);
  const [runId, setRunId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInputData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getInputs(userId);
      setInputInfo(data);
      setRunId(data.run_id);
    } catch (err) {
      setError(`Failed to fetch input data: ${err.response?.data?.detail || err.message}`);
      setInputInfo(null);
      setRunId(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateInputs = async (inputData) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        user_id: userId,
        soc_sec_benefit: parseFloat(inputData.soc_sec_benefit) || Number(INPUT_DEFAULTS.soc_sec_benefit),
        // 2026-09-13: null (not 0) when not MFJ — main.py skips nulls, so a user toggling
        // filing status keeps their spouse entries instead of having them zeroed on save.
        soc_sec_benefit_spouse: inputData.distribution_status === 'M'
          ? (parseFloat(inputData.soc_sec_benefit_spouse) || 0)
          : null,
        salary: parseFloat(inputData.salary) || Number(INPUT_DEFAULTS.salary),
        cont_return_assum: parseFloat(inputData.cont_return_assum) || Number(INPUT_DEFAULTS.cont_return_assum),
        dist_return_assum: parseFloat(inputData.dist_return_assum) || Number(INPUT_DEFAULTS.dist_return_assum),
        trad_cont_annual: 0,
        inflation_assum: parseFloat(inputData.inflation_assum) || Number(INPUT_DEFAULTS.inflation_assum),
        soc_sec_grw_assum: parseFloat(inputData.soc_sec_grw_assum) || Number(INPUT_DEFAULTS.soc_sec_grw_assum),
        retire_tax_hl: parseInt(inputData.retire_tax_hl) || Number(INPUT_DEFAULTS.retire_tax_hl),
        contribution_status: inputData.contribution_status,
        distribution_status: inputData.distribution_status,
        life_years: parseInt(inputData.life_years) || Number(INPUT_DEFAULTS.life_years),
        trad_savings: parseFloat(inputData.trad_savings),
        roth_savings: parseFloat(inputData.roth_savings),
        dist_start_age: parseInt(inputData.dist_start_age) || Number(INPUT_DEFAULTS.dist_start_age),
        soc_sec_start_age: parseInt(inputData.soc_sec_start_age) || Number(INPUT_DEFAULTS.soc_sec_start_age),
        soc_sec_start_age_spouse: inputData.distribution_status === 'M'   // 2026-09-13
          ? (parseInt(inputData.soc_sec_start_age_spouse) || Number(INPUT_DEFAULTS.soc_sec_start_age_spouse))
          : null,
        taxable_inc_when_convert: parseFloat(inputData.taxable_inc_when_convert) || Number(INPUT_DEFAULTS.taxable_inc_when_convert),
        taxable_inc_when_distribute: parseFloat(inputData.taxable_inc_when_distribute) || Number(INPUT_DEFAULTS.taxable_inc_when_distribute),
        distribution_method: inputData.distribution_method || INPUT_DEFAULTS.distribution_method,
        beneficiary_taxable_income: parseFloat(inputData.beneficiary_taxable_income) || Number(INPUT_DEFAULTS.beneficiary_taxable_income),
        beneficiary_filing_status: inputData.beneficiary_filing_status || null,   // 2026-10-27
        medicare_coverage: inputData.medicare_coverage || INPUT_DEFAULTS.medicare_coverage,
        tax_payment_source: inputData.tax_payment_source || INPUT_DEFAULTS.tax_payment_source,
        use_irs_brackets: inputData.use_irs_brackets || inputInfo?.use_irs_brackets || 'Y',
        tax_drag_assum: Number.isFinite(parseFloat(inputData.tax_drag_assum))
          ? parseFloat(inputData.tax_drag_assum)
          : Number(INPUT_DEFAULTS.tax_drag_assum),
        dist_reinvest_pct: inputData.dist_reinvest_pct != null ? parseFloat(inputData.dist_reinvest_pct) : (inputData.distribution_method === 'R' ? 0.75 : 0.2),
      };
      
      const response = await apiService.createOrUpdateInputs(payload);
      await fetchInputData(); // Refresh data
      return response;
    } catch (err) {
      setError(`Failed to update inputs: ${err.response?.data?.detail || err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [fetchInputData]);

  // 2026-03-15: Toggle tax_payment_source and re-save inputs (used by RecommendationTab toggle)
  const toggleTaxPaymentSource = async (newValue, userInfo) => {
    if (!inputInfo) return;
    const updated = { ...inputInfo, trad_savings: userInfo.trad_savings, roth_savings: userInfo.roth_savings, tax_payment_source: newValue };
    return updateInputs(updated);
  };

  // 2026-04-18: Toggle use_irs_brackets and re-save inputs (used by RecommendationTab SS torpedo toggle)
  const toggleUseIRSBrackets = async (newValue, userInfo) => {
    if (!inputInfo) return;
    const updated = { ...inputInfo, trad_savings: userInfo.trad_savings, roth_savings: userInfo.roth_savings, use_irs_brackets: newValue };
    return updateInputs(updated);
  };

  return {
    inputInfo,
    runId,
    loading,
    error,
    updateInputs,
    toggleTaxPaymentSource,
    toggleUseIRSBrackets,
    refetch: fetchInputData
  };
};

// Hook for managing conversion calculations and results
export const useConversions = (runId) => {
  const [conversions, setConversions] = useState([]);
  const [parts, setParts] = useState([]);
  const [retireYearData, setRetireYearData] = useState([]);
  const [distributionSchedule, setDistributionSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConversions = useCallback(async () => {
    if (!runId) {
      console.log('fetchConversions skipped: runId is null');
      setConversions([]);
      setParts([]);
      setRetireYearData([]);
      setDistributionSchedule(null);
      return;
    }

    console.log('Fetching conversions for runId:', runId);
    setLoading(true);
    setError(null);
    // 2026-04-03: Keep old data visible until new data arrives (avoid white flash)
    // setConversions([]);
    // setParts([]);
    // setRetireYearData([]);
    // setDistributionSchedule(null);

    try {
      // 2026-04-03: Single combined call replaces 4 separate fetches
      // const [conversionsData, partsData, retireData, distSchedule] = await Promise.all([
      //   apiService.getRothConversions(runId),
      //   apiService.getRothConversionsParts(runId),
      //   apiService.getRetireYearData(runId),
      //   apiService.getDistributionSchedule(runId)
      // ]);
      const result = await apiService.getCalculationResults(runId);

      setConversions(result.conversions);
      setParts(result.parts);
      setRetireYearData(result.retireYearData);
      setDistributionSchedule(result.distributionSchedule);
    } catch (err) {
      setError(`Failed to fetch conversion data: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  // 2026-04-02: OLD: runFullCalculation returned conversions/parts/retireYearData and set state here
  // NOW: runFullCalculation only POSTs calc. Data fetching happens via useEffect when runId updates
  // (triggered by refetchInputs() in App.js handleRunCalculations)
  const runCalculations = async (userId) => {
    setLoading(true);
    setError(null);
    // 2026-04-03: Keep old data visible until new data arrives (avoid white flash)
    // setConversions([]);
    // setParts([]);
    // setRetireYearData([]);
    // setDistributionSchedule(null);

    try {
      const result = await apiService.runFullCalculation(userId);
      // Don't set conversions/parts/retireYearData here — useEffect on runId handles it
      return result;
    } catch (err) {
      setError(`Calculation failed: ${err.message}`);
      setLoading(false);  // Reset loading on error since useEffect won't fire
      throw err;
    }
  };

  useEffect(() => {
    fetchConversions();
  }, [fetchConversions]);

  return {
    conversions,
    parts,
    retireYearData,
    distributionSchedule,
    loading,
    error,
    runCalculations,
    refetch: fetchConversions
  };
};

// Hook for managing form state and validation
export const useFormState = (initialState, validationRules = {}) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const updateField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(validationRules).forEach(field => {
      const rule = validationRules[field];
      const value = formData[field];
      
      if (rule.required && (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === ''))) {
        newErrors[field] = `${field} is required`;
      } else if (rule.pattern && value && !rule.pattern.test(value)) {
        newErrors[field] = rule.message || `Invalid ${field}`;
      } else if (rule.min !== undefined && parseFloat(value) < rule.min) {
        newErrors[field] = `${field} must be at least ${rule.min}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData(initialState);
    setErrors({});
  };

  return {
    formData,
    errors,
    updateField,
    validateForm,
    resetForm,
    setFormData
  };
};